import axios from 'axios';

// Use localhost for development
const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
const API_BASE_URL = isDevelopment ? 'http://localhost:3001' : 'https://pimbyeli.com';
const BASE_URL = `${API_BASE_URL}/api/v4/sales-percentage`;

// Create dedicated axios instance for sales percentage API
const salesApi = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
});

// Add auth interceptor
salesApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add response interceptor for better error handling
salesApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('🚨 Sales API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data
    });
    return Promise.reject(error);
  }
);

export const salesPercentageApi = {
  // Get item configuration for spreadsheet
  getItemConfig: async () => {
    try {
      console.log('🔧 API: Fetching item config from', `${BASE_URL}/item-config`);
      const response = await salesApi.get(`/api/v4/sales-percentage/item-config`);
      console.log('✅ API: Item config response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ API: Error fetching item config:', error);
      
      // Enhanced error handling
      if (error.code === 'ECONNREFUSED') {
        throw new Error('Cannot connect to server. Please ensure the backend is running on localhost:3001');
      } else if (error.response?.status === 404) {
        throw new Error('Sales percentage API endpoint not found. Please check backend routes.');
      } else if (error.response?.status === 401) {
        throw new Error('Authentication required. Please login again.');
      } else if (error.response?.status >= 500) {
        throw new Error('Server error. Please try again later.');
      }
      
      throw error;
    }
  },

  // Get sales percentage data
  getSalesData: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.locationIds && filters.locationIds.length > 0) {
        filters.locationIds.forEach(id => params.append('locationIds', id));
      }
      if (filters.percentageType) params.append('percentageType', filters.percentageType);
      if (filters.groupBy) params.append('groupBy', filters.groupBy);

      const response = await salesApi.get(`/api/v4/sales-percentage/sales-data?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching sales data:', error);
      throw error;
    }
  },

  // Get formatted data for ERPSpreadsheet component
  getSpreadsheetData: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.locationIds && filters.locationIds.length > 0) {
        filters.locationIds.forEach(id => params.append('locationIds', id));
      }
      if (filters.percentageType) params.append('percentageType', filters.percentageType);
      if (filters.metricType) params.append('metricType', filters.metricType);

      console.log('🔄 Fetching spreadsheet data with params:', params.toString());
      const response = await salesApi.get(`/api/v4/sales-percentage/spreadsheet-data?${params.toString()}`);
      console.log('📊 Spreadsheet data response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching spreadsheet data:', error);
      throw error;
    }
  },

  // Get filter options
  getFilterOptions: async () => {
    try {
      console.log('🔧 API: Fetching filter options from', `${BASE_URL}/filter-options`);
      const response = await salesApi.get(`/api/v4/sales-percentage/filter-options`);
      console.log('✅ API: Filter options response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ API: Error fetching filter options:', error);
      
      // Enhanced error handling
      if (error.code === 'ECONNREFUSED') {
        throw new Error('Cannot connect to server. Please ensure the backend is running on localhost:3001');
      } else if (error.response?.status === 404) {
        throw new Error('Filter options endpoint not found. Please check backend routes.');
      }
      
      throw error;
    }
  },

  // Health check
  healthCheck: async () => {
    try {
      const response = await salesApi.get(`/api/v4/sales-percentage/health`);
      console.log('💚 Health check passed:', response.data);
      return response.data;
    } catch (error) {
      console.error('💔 Health check failed:', error);
      throw error;
    }
  }
};

export default salesPercentageApi; 